import axios from 'axios';
import { apiBaseUrl } from 'App';
import { getCsrfToken } from './csrfFunctions';
import { loadSessionData, LoginCallbacks, LoginResponse } from './loadSessionData';

interface LoginResult {
    status: 'idle' | 'success' | 'two_factor' | 'error';
    redirect?: 'trial-wizard/1' | 'trial-homepage' | 'dashboard' | 'admin-dashboard' | 'settings/admin/environments' | 'setup-2fa';
    data?: LoginResponse;
    error?: string;
    paymentUrl?: string;
}

interface LoginParams {
    email: string;
    password: string;
    navigator_language: string;
    navigator_locale: string;
    navigator_timezone: string | null;
    remember_login: boolean;
    utc_datetime: string;
    callbacks: LoginCallbacks;
}

// Log in to the server
export const login = async ({ 
    email, 
    password, 
    navigator_language, 
    navigator_locale, 
    navigator_timezone, 
    utc_datetime, 
    remember_login, 
    callbacks 
}: LoginParams): Promise<LoginResult> => {
    // Get the csrf token
    const csrfToken = getCsrfToken();

    try {
        // Send login request
        const response = await axios.post<LoginResponse & { two_factor_pending?: boolean }>(
            `${apiBaseUrl}/login/`,
            {
                email,
                password,
                navigator_language,
                navigator_locale,
                navigator_timezone,
                utc_datetime,
                remember_login
            },
            {
                withCredentials: true,
                headers: {
                    'X-CSRFToken': csrfToken
                }
            }
        );

        // Return the two factor state so the frontend shows the code screen
        if (response.data.two_factor_pending) {
            return { status: 'two_factor' };
        }

        // Load the session data
        loadSessionData({
            responseData: response.data,
            callbacks
        });

        // Return success
        return { 
            status: 'success', 
            redirect: response.data.environment_data.redirect_to,
            data: response.data 
        };
    } catch (error: any) {
        // Configure the error message
        const errorMessage = error.response?.data?.general || error.message || 'Error while logging in';

        // Return the error with the payment url when the environment is blocked for payment
        return {
            status: 'error',
            error: errorMessage,
            paymentUrl: error.response?.data?.payment_url
        }
    }
}